import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronLeftIcon, ChevronRightIcon } from '@heroicons/react/24/outline';

const DRAG_THRESHOLD = 6;
// Fraction of the slide width a mouse drag needs to cover to change slide.
const DRAG_FLIP_RATIO = 0.15;

const toUrl = (m) => (typeof m === 'string' ? m : m?.url || m?.src || m?.path);

// Image strip for the brand cards. Uses native horizontal scroll + CSS snap so
// touch swipes get the platform's momentum and never fight vertical page
// scroll; mouse users get drag-to-swipe and arrows on hover.
const BrandHeroCarousel = ({ images, className = '' }) => {
  const { t } = useTranslation();
  const urls = useMemo(
    () => (Array.isArray(images) ? images.map(toUrl).filter(Boolean) : []),
    [images]
  );
  const count = urls.length;

  const rootRef = useRef(null);
  const trackRef = useRef(null);
  const rafRef = useRef(0);
  const dragRef = useRef(null);
  const suppressClickRef = useRef(false);

  const [active, setActive] = useState(0);
  // Slides past this index render a placeholder until they're about to be seen,
  // so a long list of cards doesn't download every brand's whole gallery.
  const [loadedUpTo, setLoadedUpTo] = useState(0);

  useEffect(() => {
    setLoadedUpTo((n) => Math.max(n, active + 1));
  }, [active]);

  // Preload the second image once the card is near the viewport so the first
  // swipe never lands on a blank slide.
  useEffect(() => {
    if (count < 2 || !rootRef.current) return undefined;
    if (typeof IntersectionObserver === 'undefined') {
      setLoadedUpTo((n) => Math.max(n, 1));
      return undefined;
    }
    const io = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setLoadedUpTo((n) => Math.max(n, 1));
          io.disconnect();
        }
      },
      { rootMargin: '200px' }
    );
    io.observe(rootRef.current);
    return () => io.disconnect();
  }, [count]);

  useEffect(() => () => cancelAnimationFrame(rafRef.current), []);

  const handleScroll = () => {
    cancelAnimationFrame(rafRef.current);
    rafRef.current = requestAnimationFrame(() => {
      const el = trackRef.current;
      if (!el || !el.clientWidth) return;
      const idx = Math.round(el.scrollLeft / el.clientWidth);
      setActive(Math.min(Math.max(idx, 0), count - 1));
    });
  };

  const goTo = (i) => {
    const el = trackRef.current;
    if (!el) return;
    const target = Math.min(Math.max(i, 0), count - 1);
    setLoadedUpTo((n) => Math.max(n, target + 1));
    el.scrollTo({ left: target * el.clientWidth, behavior: 'smooth' });
  };

  // Mouse drag-to-swipe. Touch and pen are left to native scrolling.
  const onPointerDown = (e) => {
    if (e.pointerType !== 'mouse' || e.button !== 0) return;
    dragRef.current = {
      id: e.pointerId,
      startX: e.clientX,
      startLeft: trackRef.current.scrollLeft,
      moved: false,
    };
  };

  const onPointerMove = (e) => {
    const d = dragRef.current;
    const el = trackRef.current;
    if (!d || !el) return;
    const dx = e.clientX - d.startX;
    if (!d.moved) {
      if (Math.abs(dx) < DRAG_THRESHOLD) return;
      d.moved = true;
      el.setPointerCapture?.(d.id);
      // Snap would fight the manual scrollLeft updates while dragging.
      el.style.scrollSnapType = 'none';
      setLoadedUpTo((n) => Math.max(n, active + 2));
    }
    el.scrollLeft = d.startLeft - dx;
  };

  const endDrag = (e) => {
    const d = dragRef.current;
    const el = trackRef.current;
    dragRef.current = null;
    if (!d || !d.moved || !el) return;

    el.releasePointerCapture?.(d.id);
    // The click that follows this pointerup must not open the brand page.
    suppressClickRef.current = true;
    setTimeout(() => {
      suppressClickRef.current = false;
    }, 0);

    const width = el.clientWidth || 1;
    const dx = (e?.clientX ?? d.startX) - d.startX;
    let target = Math.round(d.startLeft / width);
    if (Math.abs(dx) > width * DRAG_FLIP_RATIO) target += dx < 0 ? 1 : -1;
    target = Math.min(Math.max(target, 0), count - 1);

    const restoreSnap = () => {
      el.style.scrollSnapType = '';
    };
    if ('onscrollend' in window) {
      el.addEventListener('scrollend', restoreSnap, { once: true });
    }
    // scrollend doesn't fire when there's nothing left to scroll.
    setTimeout(restoreSnap, 600);
    el.scrollTo({ left: target * width, behavior: 'smooth' });
  };

  const onClickCapture = (e) => {
    if (suppressClickRef.current) {
      e.preventDefault();
      e.stopPropagation();
      suppressClickRef.current = false;
    }
  };

  const stop = (fn) => (e) => {
    e.preventDefault();
    e.stopPropagation();
    fn();
  };

  if (count === 0) {
    return (
      <div className={`relative ${className}`}>
        <div className="absolute inset-0 bg-gradient-to-br from-brand-soft via-brand to-brand-deep" />
      </div>
    );
  }

  const arrowClass =
    'absolute top-1/2 -translate-y-1/2 z-[2] w-10 h-10 flex items-center justify-center ' +
    'transition-opacity duration-200 focus:outline-none ' +
    '[@media(hover:hover)]:opacity-0 [@media(hover:hover)]:group-hover/hero:opacity-100 ' +
    'focus-visible:opacity-100';
  const arrowFaceClass =
    'w-7 h-7 rounded-full bg-white/75 backdrop-blur-sm text-ink-strong shadow-sm ring-1 ring-black/5 ' +
    'flex items-center justify-center transition group-focus-visible/arrow:ring-2 group-focus-visible/arrow:ring-brand ' +
    'hover:bg-white';

  return (
    <div ref={rootRef} className={`relative group/hero bg-paper ${className}`}>
      <div
        ref={trackRef}
        onScroll={count > 1 ? handleScroll : undefined}
        onPointerDown={count > 1 ? onPointerDown : undefined}
        onPointerMove={count > 1 ? onPointerMove : undefined}
        onPointerUp={count > 1 ? endDrag : undefined}
        onPointerCancel={count > 1 ? endDrag : undefined}
        onClickCapture={onClickCapture}
        className="absolute inset-0 flex overflow-x-auto overflow-y-hidden snap-x snap-mandatory overscroll-x-contain no-scrollbar select-none"
      >
        {urls.map((src, i) => (
          <div
            key={`${src}-${i}`}
            className="relative h-full w-full shrink-0 snap-start snap-always bg-paper-2"
          >
            {i <= loadedUpTo && (
              <img
                src={src}
                alt=""
                draggable={false}
                className="absolute inset-0 w-full h-full object-cover"
                loading="lazy"
                decoding="async"
              />
            )}
          </div>
        ))}
      </div>

      <div className="absolute inset-0 bg-gradient-to-b from-transparent to-black/30 pointer-events-none" />

      {count > 1 && (
        <>
          {active > 0 && (
            <button
              type="button"
              aria-label={t('listing.prev_image')}
              onClick={stop(() => goTo(active - 1))}
              onKeyDown={(e) => e.stopPropagation()}
              className={`${arrowClass} group/arrow left-1`}
            >
              <span className={arrowFaceClass}>
                <ChevronLeftIcon className="w-4 h-4" strokeWidth={2.25} />
              </span>
            </button>
          )}
          {active < count - 1 && (
            <button
              type="button"
              aria-label={t('listing.next_image')}
              onClick={stop(() => goTo(active + 1))}
              onKeyDown={(e) => e.stopPropagation()}
              className={`${arrowClass} group/arrow right-1`}
            >
              <span className={arrowFaceClass}>
                <ChevronRightIcon className="w-4 h-4" strokeWidth={2.25} />
              </span>
            </button>
          )}

          <div
            className="absolute bottom-2.5 left-1/2 -translate-x-1/2 z-[2] pointer-events-none"
            aria-live="polite"
            aria-label={t('listing.image_position', {
              current: active + 1,
              total: count,
            })}
          >
            <div className="flex items-center gap-1.5">
              {urls.map((src, i) => (
                <span
                  key={`${src}-dot-${i}`}
                  className={`block rounded-full transition-all duration-200 shadow-[0_0_2px_rgba(0,0,0,.35)] ${
                    i === active
                      ? 'w-2 h-2 bg-white'
                      : 'w-1.5 h-1.5 bg-white/60'
                  }`}
                />
              ))}
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default BrandHeroCarousel;
